import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import SmoothScroll from "@/components/SmoothScroll";
import MarketingScripts, { GTMNoScript } from "@/components/MarketingScripts";
import Navbar from "@/components/Navbar";
import CartSheet from "@/components/CartSheet";
import MobileBottomNav from "@/components/MobileBottomNav";
import CompareBar from "@/components/CompareBar";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import CookieConsent from "@/components/CookieConsent";
import AgeGate from "@/components/AgeGate";
import { captureRefFromUrl } from "@/lib/referral";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Compare from "@/pages/Compare";
import Quiz from "@/pages/Quiz";
import AuthCallback from "@/pages/AuthCallback";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Disclaimer from "@/pages/Disclaimer";
import Shipping from "@/pages/Shipping";
import FAQ from "@/pages/FAQ";
import Partners from "@/pages/Partners";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Capture referral code on first mount + any route change (handles ?ref=X links)
    captureRefFromUrl();
    if (hash) {
      // Wait a tick for the target route to mount, then smooth-scroll to anchor
      const id = hash.replace("#", "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Process Google OAuth callback FIRST (session_id in URL fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <SmoothScroll>
      <ScrollToTop />
      <AnnouncementBanner />
      <Navbar />
      <CartSheet />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Catalog />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<Account />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/quiz" element={<Quiz />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/partners" element={<Partners />} />
      </Routes>
      <MobileBottomNav />
      <CompareBar />
      <CookieConsent />
      <AgeGate />
      <Toaster position="bottom-right" theme="dark" />
    </SmoothScroll>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0F0F10] text-[#F5F5F5] font-body">
      <HelmetProvider>
        <MarketingScripts />
        <GTMNoScript />
        <div className="grain-overlay" />
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <CompareProvider>
                <AppRoutes />
              </CompareProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
